import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import type { CrmPeriodDetails } from "@/hooks/api/useAdminViews";
import { cn } from "@/lib/cn";
import { formatMonth, formatCurrency } from "./formatters";

const COLORS = { renewal: "#D0A84C", new: "#F0D98E", refunded: "#EF4444", labCost: "#7C9AFF" };

type Row = {
  month: string;
  newCustomerRevenue: number;
  renewalRevenue: number;
  refunded: number;
  labCost: number;
};

export function RevenueChart({ monthlySeries }: { monthlySeries: CrmPeriodDetails["monthlySeries"] }) {
  const raw: Row[] = monthlySeries.map((m) => {
    const revenue = parseFloat(m.revenue) / 100;
    const newCustomerRevenue = parseFloat(m.newCustomerRevenue) / 100;
    return {
      month: formatMonth(m.month),
      newCustomerRevenue,
      renewalRevenue: revenue - newCustomerRevenue,
      refunded: parseFloat(m.refunded) / 100,
      labCost: parseFloat(m.labCost) + parseFloat(m.labDirectCost ?? "0") + parseFloat(m.labFacilityCost ?? "0"),
    };
  });

  // Trim leading months with no data so "All tid" starts where activity begins
  const firstNonEmpty = raw.findIndex(
    (d) => d.newCustomerRevenue !== 0 || d.renewalRevenue !== 0 || d.refunded !== 0 || d.labCost !== 0,
  );
  const data = firstNonEmpty > 0 ? raw.slice(firstNonEmpty) : raw;

  // Only show chart for multi-month ranges
  if (data.length < 2) return null;

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#3B3B48" />
        <XAxis dataKey="month" tick={{ fill: "#a1a1aa", fontSize: 12 }} axisLine={{ stroke: "#3B3B48" }} />
        <YAxis tick={{ fill: "#a1a1aa", fontSize: 12 }} axisLine={{ stroke: "#3B3B48" }} />
        <Tooltip content={<RevenueTooltip />} cursor={{ fill: "var(--color-admin-card-hover)" }} />
        <Legend wrapperStyle={{ color: "#a1a1aa", fontSize: 12 }} />
        {/* One stacked bar: its height is still the month's total omsetning. */}
        <Bar dataKey="renewalRevenue" stackId="revenue" fill={COLORS.renewal} name="Omsetning — fornyede" />
        <Bar dataKey="newCustomerRevenue" stackId="revenue" fill={COLORS.new} name="Omsetning — nye kunder" radius={[4, 4, 0, 0]} />
        <Bar dataKey="refunded" fill={COLORS.refunded} name="Refundert" radius={[4, 4, 0, 0]} />
        <Bar dataKey="labCost" fill={COLORS.labCost} name="Labkost" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Every figure for the hovered month, including the stack's total, which no bar segment shows on its own. */
function RevenueTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const row: Row = payload[0].payload;
  const lines = [
    { label: "Nye kunder", value: row.newCustomerRevenue, color: COLORS.new },
    { label: "Fornyede", value: row.renewalRevenue, color: COLORS.renewal },
    { label: "Omsetning totalt", value: row.newCustomerRevenue + row.renewalRevenue, divider: true },
    { label: "Refundert", value: row.refunded, color: COLORS.refunded, divider: true },
    { label: "Labkost", value: row.labCost, color: COLORS.labCost },
  ];

  return (
    <div className="rounded-md border border-admin-card-border bg-admin-card px-3 py-2 shadow-lg">
      <p className="text-xs text-admin-text-subtle mb-1">{row.month}</p>
      {lines.map((l) => (
        <p
          key={l.label}
          className={cn("flex items-center gap-1.5 text-xs", l.divider && "mt-1 pt-1 border-t border-admin-card-border")}
        >
          <span className="w-2.5 h-0.5 rounded-full" style={{ background: l.color ?? "transparent" }} />
          <span className="font-medium tabular-nums text-admin-text-primary">{formatCurrency(l.value)}</span>
          <span className="text-admin-text-secondary">{l.label}</span>
        </p>
      ))}
    </div>
  );
}
